import {
  Grid,
  makeStyles,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  InputField,
  Typography,
  ConfirmationDialog,
  IconButton
} from '@paintscout/ui'
import { useQuote, useWisetack } from '@paintscout/react-quote'
import type { QuoteTag, QuoteDocument, QuoteStatus } from 'paintscout'
import type { QuoteQuery } from '@paintscout/api'
import CloseIcon from '@material-ui/icons/Close'
import type { DialogProps } from '@paintscout/ui'
import {
  SingleDatePicker,
  TileList,
  TagSelection,
  DropdownSelect,
  useDialogs,
  useUser,
  useClientOptions
} from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import {
  getFeatures,
  getPipelines,
  getQuoteTags,
  uuid,
  bulkUpdateQuoteTags,
  getObjectLabels,
  getQuoteTypeOptions,
  getQuoteTemplateOptions,
  setQuoteType,
  setStatus as setStatusOnQuote,
  setPresentationTemplate,
  updateToLatestTerms,
  sanitizeTemplate,
  getInitialPresentation,
  getInitialTerms,
  getTags,
  getTagKeys,
  getLeadSources
} from '@paintscout/util/builder'
import { QuoteDocument as QuoteGQLDocument, useDealQuery, useUpdateDealStageMutation } from '@paintscout/api'
import QuoteStatusSelection from 'src/components/common/QuoteStatusSelection'
import find from 'lodash/find'
import type { Moment } from 'moment'
import moment from 'moment'
import React, { useState, useRef, useEffect } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import { getIndefiniteArticle, isEmpty } from '@paintscout/util/util'
import { track } from 'shared/util/analytics'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { goToDeal } from 'src/util/routing'
import LeadSourceSelector from '../ContactDialog/LeadSourceSelector'

export interface EditQuoteInfoDialogProps extends DialogProps {
  onCancel: () => void
  onConfirm: () => void
}

export const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  formControl: {},
  dialogContent: {
    minHeight: 400
  },
  customerNote: {
    background: theme.paintscout.palette.brand.Neutral.P96,
    padding: theme.spacing(2),
    borderRadius: theme.borderRadius.md
  },
  pipelineInputContainer: {
    display: 'flex',
    gap: '16px',
    padding: theme.spacing(),
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column'
    }
  },
  pipelineInput: {
    flex: '1 1 0%',
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  pipelineButton: {
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  quoteTypeContainer: {
    overflowY: 'hidden',
    [theme.breakpoints.down('xs')]: {
      overflowY: 'visible',
      marginTop: theme.spacing(2)
    }
  }
}))

/**
 * Allows editing of quote type, status, and other misc. info that doesn't really fit anywhere else
 */
function EditQuoteInfoDialog(props: EditQuoteInfoDialogProps) {
  const { open, onConfirm, onCancel, ...baseDialogProps } = props
  const { options, save } = useClientOptions()

  const apolloClient = useApolloClient()
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const { quote, updateQuote } = useQuote()
  const { user, isSuperadmin, isSupport, preferences } = useUser()
  const { hasWisetack } = useWisetack({ user, options, preferences })
  const classes = useStyles(props)
  const pipelines = getPipelines({ options })
  const [loadingTemplate, setLoadingTemplate] = useState(false)
  const { crm: crmFlag, productionFlag, canDeactivateSettingsItems } = useFlags()
  const quoteTypeOptions = getQuoteTypeOptions({ options, deleted: true, canDeactivate: !!canDeactivateSettingsItems })
  const hasConvertFeature = isSuperadmin || isSupport || (options?.options?.features?.quotes?.convertToInvoice ?? false)
  const objectLabels = getObjectLabels({ options, invoice: quote?.is_invoice })
  const allowConvert = (!quote?.child_invoices || quote?.child_invoices?.length == 0) && hasConvertFeature
  const quoteLabel = getObjectLabels({ options, invoice: false }).quote
  const invoiceLabel = getObjectLabels({ options, invoice: true }).quote
  const { scheduledDate: hasScheduledDate, changeAcceptanceDate, smartTemplate } = getFeatures({ options }).quotes
  const features = getFeatures({ options })
  const hasTags = !!(features.quotes?.tags?.enabled || crmFlag)
  const hasSources = !!features.quotes?.quoteLeadSources?.enabled
  const quoteSources = getLeadSources({ options, type: 'leadSources' })
  const [currentQuoteSource, setCurrentQuoteSource] = useState<DropdownSelectOption>({
    value: quote?.lead_source ? quote?.lead_source : '',
    label: quote?.lead_source ? quoteSources.find((source) => source.name === quote?.lead_source)?.label : ''
  })
  const [selectedType, setSelectedType] = useState(find(quoteTypeOptions, { key: quote.int_ext.value }))
  // if our selected type has been deleted, need to include it in the options(but not other deleted types)
  // and hide them all if selected type is not deleted
  const quoteTypeListOptions = selectedType?.deleted
    ? quoteTypeOptions.filter((item) => !item.deleted || item.key === selectedType.key)
    : quoteTypeOptions.filter((item) => !item.deleted)
  const { data, loading: dealLoading } = useDealQuery({
    variables: {
      id: quote?.deal_id
    },
    skip: !quote?.deal_id,
    fetchPolicy: 'network-only'
  })
  const [updateDealStage] = useUpdateDealStageMutation({
    fetchPolicy: 'no-cache', // to avoid caching the updated deal which would refire the rawData useEffect
    refetchQueries: ['deal']
  })
  const initialPipelineId = data?.deal?.pipeline?.value ?? null
  const initialPipeline = pipelines.find((pipeline) => pipeline.key === initialPipelineId)
  const initialPipelineLabel = initialPipeline?.label ?? ''
  const initialStageId = data?.deal?.stage?.value ?? null
  const initialStage = initialPipeline?.stages.find((stage) => stage.key === initialStageId)
  const initialStageLabel = initialStage?.label ?? ''
  const [currentPipeline, setCurrentPipeline] = useState<DropdownSelectOption>({
    value: initialPipelineId,
    label: initialPipelineLabel
  })
  const [currentStage, setCurrentStage] = useState<DropdownSelectOption>({
    value: initialStageId,
    label: initialStageLabel
  })

  useEffect(() => {
    if (initialPipelineId) {
      setCurrentPipeline({
        value: initialPipelineId,
        label: initialPipelineLabel
      })
      setCurrentStage({
        value: initialStageId,
        label: initialStageLabel
      })
    }
  }, [initialPipelineId])
  // confirm is enabled by default if a quote's type option has been deleted
  // othereise, it's disabled unless a type is selected
  const [disableConfirm, setDisableConfirm] = useState(false)

  const handleSetSelectedType = (key: string | string[]) => {
    if (Array.isArray(key)) {
      setSelectedType(null)
      setDisableConfirm(true)
    } else {
      const newType = find(quoteTypeOptions, { key })
      setDisableConfirm(false)
      setSelectedType(newType)
    }
  }

  const quoteTemplateOptions = getQuoteTemplateOptions({
    options,
    quoteType: selectedType?.key,
    canDeactivate: !!canDeactivateSettingsItems
  })
  const [selectedTemplate, setSelectedTemplate] = useState(find(quoteTemplateOptions, { key: quote.fromTemplate?.key }))

  const [status, setStatus] = useState({ value: quote.status.value, note: quote.status.note?.value })
  const statusValueHasChanged = status.value !== quote.status.value
  const statusNoteHasChanged = status.note !== quote.status.note?.value
  const statusHasChanged = statusNoteHasChanged || statusValueHasChanged
  const rawQuoteTagDetails = quote?.tagDetails ?? []
  const allTagKeys = getTagKeys({ quote, includeDeleted: true })
  const appliedTagKeys = getTagKeys({ quote })
  const startingTags = useRef<QuoteTag[]>(getTags({ options, keys: allTagKeys }))
  const [appliedTags, setAppliedTags] = useState<QuoteTag[]>(getTags({ options, keys: appliedTagKeys }))

  const hasInitialAcceptedDate = !!quote?.dates?.client?.accepted
  const hasInitialScheduledDate = !!quote?.dates?.scheduled
  const hasInitialCompletionDate = !!quote?.dates?.completion

  const initialAcceptedDate = hasInitialAcceptedDate ? moment(quote.dates.client.accepted) : null
  const initialScheduledDate = hasInitialScheduledDate ? moment(quote.dates.scheduled) : null
  const initialCompletionDate = hasInitialCompletionDate ? moment(quote.dates.completion) : null

  // Store the default values we use when initializing (created once, stored in refs to avoid re-creation)
  const defaultAcceptedDateRef = useRef<Moment>(moment())
  const defaultScheduledDateRef = useRef<Moment>(moment().add(1, 'days'))
  const defaultCompletionDateRef = useRef<Moment>(moment().add(1, 'days'))

  const [acceptedDate, setAcceptedDate] = useState<Moment>(initialAcceptedDate || defaultAcceptedDateRef.current)
  const [scheduledDate, setScheduledDate] = useState<Moment>(initialScheduledDate || defaultScheduledDateRef.current)
  const [completionDate, setCompletedDate] = useState<Moment>(initialCompletionDate || defaultCompletionDateRef.current)

  // Compare dates, handling null/undefined cases
  // If original quote had a date, compare to that. If not, only count as changed if different from the initial default.
  const acceptedDateChanged = hasInitialAcceptedDate
    ? initialAcceptedDate!.unix() !== acceptedDate.unix()
    : acceptedDate.unix() !== defaultAcceptedDateRef.current.unix()
  const scheduledDateChanged = hasInitialScheduledDate
    ? initialScheduledDate!.unix() !== scheduledDate.unix()
    : scheduledDate.unix() !== defaultScheduledDateRef.current.unix()
  const completionDateChanged = hasInitialCompletionDate
    ? initialCompletionDate!.unix() !== completionDate.unix()
    : completionDate.unix() !== defaultCompletionDateRef.current.unix()

  const loading = loadingTemplate || props.loading

  const isInvoice = quote?.is_invoice

  const primaryLabel = isInvoice ? invoiceLabel : quoteLabel
  const secondaryLabel = isInvoice ? quoteLabel : invoiceLabel

  const hasChanges =
    statusHasChanged ||
    acceptedDateChanged ||
    scheduledDateChanged ||
    completionDateChanged ||
    selectedType?.key !== quote.int_ext.value ||
    selectedTemplate?.key !== quote.fromTemplate?.key ||
    currentPipeline?.value !== initialPipelineId ||
    currentStage?.value !== initialStageId ||
    JSON.stringify(appliedTags.map((t) => t.name).sort()) !==
      JSON.stringify(startingTags.current.map((t) => t.name).sort())

  const handleClose = () => {
    if (hasChanges) {
      openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. Are you sure you want to close?',
        onConfirm: () => {
          dismissDialog() // Dismiss confirmation dialog
          dismissAllDialogs() // Close all dialogs
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    } else {
      if (onCancel) {
        onCancel()
      } else {
        dismissAllDialogs()
      }
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={'md'} fullWidth={true} {...baseDialogProps}>
      <DialogTitle
        loading={loading}
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        }
      >
        Edit {objectLabels.quote.value}
      </DialogTitle>

      <DialogContent classes={{ root: classes.dialogContent }}>
        <form
          id="edit-quote-info-form"
          onSubmit={async (e) => {
            e.preventDefault()

            const existingTags = getQuoteTags({ options })
            const createdTags = appliedTags.filter(
              (tag) => !existingTags.some((existingTag) => existingTag.name === tag.name)
            )

            if (createdTags.length > 0) {
              const updatedOptions = bulkUpdateQuoteTags(createdTags, options)

              await save({ options: updatedOptions })
            }

            const pipelineStageChanged = currentPipeline?.value !== initialPipelineId
            const pipelineChanged = currentStage?.value !== initialStageId

            if (data?.deal?._id && (pipelineStageChanged || pipelineChanged)) {
              updateDealStage({
                variables: {
                  dealId: data.deal._id,
                  stage: currentStage?.value
                }
              })
            }

            const newTagNames = appliedTags.map((tag) => tag.name)
            const startingTagNames = startingTags.current.map((tag) => tag.name)
            const deletedTagNames = startingTagNames.filter((name) => !newTagNames.includes(name))
            const deletedTagKeys = appliedTagKeys.filter((name) => deletedTagNames.includes(name))
            const reAddedTagNames = appliedTagKeys.filter((name) => newTagNames.includes(name))

            const updated = Date.now()
            const updatedTags = [
              ...appliedTags,
              ...deletedTagKeys.map((name) => {
                return { name }
              })
            ].map((tag) => {
              const updatedValue =
                rawQuoteTagDetails.filter((rawTag) => rawTag.name == tag.name && reAddedTagNames.includes(tag.name))[0]
                  ?.updated ?? updated
              const deltedValue = deletedTagNames.includes(tag.name)
              return {
                name: tag.name,
                updated: updatedValue,
                deleted: deltedValue
              }
            })

            let updatedQuote: QuoteDocument = {
              ...quote,
              tags: appliedTags.map((tag) => tag.name),
              tagDetails: updatedTags,
              lead_source: currentQuoteSource?.value
            }

            const tagsHaveChanged =
              quote?.tags?.length !== updatedQuote?.tags?.length ||
              quote?.tags?.reduce((prev, current) => {
                if (updatedQuote.tags.includes(current)) {
                  return prev
                } else {
                  return true
                }
              }, false)

            if (statusHasChanged) {
              updatedQuote = setStatusOnQuote({
                quote: updatedQuote,
                status: status.value,
                note: status.note,
                acceptedVia: 'editQuoteInfo',
                updateDates: statusValueHasChanged
              })
            }

            if (tagsHaveChanged) {
              updatedQuote = {
                ...updatedQuote,
                dates: {
                  ...updatedQuote.dates,
                  lastTagUpdate: Date.now()
                }
              }
            }

            if (selectedType?.key && selectedType?.key !== updatedQuote.int_ext?.value) {
              updatedQuote = setQuoteType({ quote: updatedQuote, options, quoteType: selectedType.key })
            }

            const unixAcceptedDate = acceptedDate.unix() * 1000
            if (
              quote?.dates?.client?.accepted &&
              unixAcceptedDate !== quote?.dates?.client?.accepted &&
              (statusValueHasChanged || acceptedDateChanged)
            ) {
              updatedQuote.dates.client.accepted = unixAcceptedDate
              updatedQuote.status.history.accepted.timestamp = unixAcceptedDate
              updatedQuote.status.history.accepted.day = new Date(unixAcceptedDate).setHours(0, 0, 0, 0)
            }

            const unixScheduledDate = scheduledDate.unix() * 1000
            if (unixScheduledDate !== quote?.dates?.scheduled && scheduledDateChanged) {
              updatedQuote.dates.scheduled = unixScheduledDate
            }

            const unixCompletionDate = completionDate.unix() * 1000
            if (unixCompletionDate !== quote?.dates?.completion && completionDateChanged) {
              updatedQuote.dates.completion = unixCompletionDate
            }

            if (selectedTemplate?.key && selectedTemplate?.key !== updatedQuote.fromTemplate?.key) {
              setLoadingTemplate(true)
              openDialog(ConfirmationDialog, {
                message: (
                  <>
                    <Typography>
                      Changing the template of this {objectLabels.quote.value.toLowerCase()} will replace all items
                      (Areas & Line Items).
                    </Typography>
                    <Typography>&nbsp;</Typography>
                    <Typography>
                      {objectLabels.quote.value} Contact, Job Address, ID, Date, and Notes will not be changed.
                    </Typography>
                  </>
                ),
                scary: true,
                onConfirm: async () => {
                  dismissDialog()
                  const presentationTemplate = getInitialPresentation({ options, quoteType: selectedType })
                  const template = await apolloClient
                    .query<QuoteQuery>({
                      query: QuoteGQLDocument,
                      variables: {
                        id: selectedTemplate.key
                      }
                    })
                    .then((res) => res.data?.quote)

                  const sanitizedTemplate = sanitizeTemplate({
                    smartTemplate,
                    template: { ...selectedTemplate, content: template },
                    options
                  }) as QuoteDocument

                  updatedQuote = {
                    ...updatedQuote,
                    ...sanitizedTemplate,
                    presentationTemplate: setPresentationTemplate({ template, presentationTemplate, options }),
                    terms: template?.content?.terms
                      ? updateToLatestTerms({ terms: template.content.terms, options })
                      : updatedQuote.terms,
                    fromTemplate: selectedTemplate,
                    status: updatedQuote.status,
                    estimatorNotes: !isEmpty(updatedQuote.estimatorNotes)
                      ? updatedQuote.estimatorNotes
                      : sanitizedTemplate.estimatorNotes ?? ''
                  }

                  updateQuote({ quote: updatedQuote, autosave: true })
                  track('Changed Estimate Template via Dialog')

                  onConfirm()
                },
                onCancel: () => {
                  dismissDialog()
                  setLoadingTemplate(false)
                }
              })
            } else {
              updateQuote({ quote: updatedQuote, autosave: true })
              onConfirm()
            }
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <QuoteStatusSelection
                    variant="single"
                    label="Status"
                    fullWidth
                    invoice={isInvoice}
                    allowInvoiced={quote.status.value === 'accepted' || quote.status.value === 'invoiced'}
                    value={status.value}
                    onChange={(newStatus: DropdownSelectOption) => {
                      openDialog(ConfirmationDialog, {
                        message: (
                          <>
                            <Typography>
                              Please note that manually changing the status of an{' '}
                              {quote.is_invoice ? 'invoice' : objectLabels.quote.value.toLowerCase()} will not trigger
                              any automatic thank-you emails or notifications.
                            </Typography>
                          </>
                        ),
                        onConfirm: async () => {
                          setStatus({
                            ...status,
                            value: newStatus.value as QuoteStatus
                          })
                          dismissDialog()
                        },
                        onCancel: () => {
                          dismissDialog()
                        }
                      })
                    }}
                  />
                </Grid>

                {status.value === 'accepted' && changeAcceptanceDate && (
                  <Grid item xs={12} md={4}>
                    <SingleDatePicker
                      label={'Date Accepted'}
                      date={acceptedDate}
                      onChange={(date) => setAcceptedDate(date)}
                    />
                  </Grid>
                )}
                {status.value === 'accepted' && (hasScheduledDate || hasWisetack) && (
                  <Grid item xs={12} md={4}>
                    <SingleDatePicker
                      label={'Scheduled Date'}
                      date={scheduledDate}
                      onChange={(date) => setScheduledDate(date)}
                      PopperProps={{
                        placement: 'bottom-end'
                      }}
                    />
                  </Grid>
                )}
                {isInvoice && (hasScheduledDate || hasWisetack) && (
                  <Grid item xs={12} md={4}>
                    <SingleDatePicker
                      label={'Completion Date'}
                      date={completionDate}
                      onChange={(date) => setCompletedDate(date)}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              {/* show if status has changed, or note exists */}
              {/* <Collapse show={status.value !== quote.status.value || !!status.note}> */}
              <InputField
                label="Status Change Reason"
                placeholder="What is the reason for the status change?"
                value={status.note ?? ''}
                fullWidth
                // required={status.value !== quote.status.value}
                multiline
                onChange={(ev) => {
                  setStatus({
                    ...status,
                    note: ev.target.value
                  })
                }}
              />
              {/* </Collapse> */}
            </Grid>
            {crmFlag && initialPipeline && (
              <div className={classes.pipelineInputContainer}>
                {productionFlag && (
                  <div className={classes.pipelineInput}>
                    <DropdownSelect
                      label="Pipeline"
                      searchable
                      variant="single"
                      value={currentPipeline}
                      options={pipelines.map((pipeline) => ({
                        value: pipeline.key,
                        label: pipeline.label
                      }))}
                      onChange={(option) => {
                        setCurrentPipeline(option)
                        const selectedPipeline = pipelines.find((pipeline) => pipeline.key === option.value)
                        const firstStage = selectedPipeline?.stages[0]
                        setCurrentStage({
                          value: firstStage?.key,
                          label: firstStage?.label
                        })
                      }}
                      fullWidth
                      disabled={dealLoading}
                      loading={dealLoading}
                    />
                  </div>
                )}

                <div className={classes.pipelineInput}>
                  <DropdownSelect
                    label="Stage"
                    searchable
                    variant="single"
                    value={currentStage}
                    options={
                      pipelines
                        .find((pipeline) => pipeline.key === currentPipeline?.value)
                        ?.stages?.map((stage) => ({
                          value: stage.key,
                          label: stage.label
                        })) ?? []
                    }
                    onChange={(option) => {
                      setCurrentStage(option)
                    }}
                    fullWidth
                    disabled={dealLoading}
                    loading={dealLoading}
                  />
                </div>
                <Button
                  variant="dialog"
                  onClick={() => {
                    goToDeal(quote.deal_id)
                  }}
                  disabled={dealLoading}
                  className={classes.pipelineButton}
                >
                  View Deal
                </Button>
              </div>
            )}

            {hasTags && (
              <Grid item xs={12}>
                <TagSelection
                  label="Tags"
                  appliedTags={appliedTags || []}
                  fullWidth
                  handleChange={(updatedTags: QuoteTag[]) => {
                    setAppliedTags(updatedTags)
                  }}
                  onCreateOption={(label: string) => {
                    const tag: QuoteTag = {
                      name: uuid(),
                      label: label,
                      color: options.options.primaryColor.value.toString()
                    }
                    setAppliedTags([...appliedTags, tag])
                  }}
                />
              </Grid>
            )}
            {!isInvoice && hasSources && (
              <Grid item xs={12}>
                <LeadSourceSelector
                  value={currentQuoteSource}
                  sources={quoteSources.map((source) => ({
                    value: source.name,
                    label: source.label
                  }))}
                  onChange={(option) => setCurrentQuoteSource(option)}
                />
              </Grid>
            )}
            {quote?.status?.customerNote?.value && (
              <Grid item xs={12}>
                <FormLabel>Customer Note/Reason</FormLabel>
                <div className={classes.customerNote}>
                  <Typography>{quote.status.customerNote.value}</Typography>
                </div>
              </Grid>
            )}
            <Grid item xs={12} className={classes.quoteTypeContainer}>
              <TileList
                title={`${objectLabels.quote.value} Type`}
                variant="select"
                subtitle={`Rates, Line Items, and Areas are filtered by ${objectLabels.quote.value.toLowerCase()} type`}
                items={quoteTypeListOptions}
                selected={selectedType?.key}
                onSelect={(ev, key) => handleSetSelectedType(key)}
              />
            </Grid>

            <Grid item xs={12}>
              <TileList
                title={`${objectLabels.quote.value} Template`}
                variant="select"
                items={quoteTemplateOptions}
                selected={selectedTemplate?.key}
                onSelect={(ev, key) => {
                  setSelectedTemplate(quoteTemplateOptions.find((i) => i.key === key))
                }}
              />
            </Grid>

            {allowConvert && (
              <Grid item xs={12} container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant={'overline'}>{`Convert to ${secondaryLabel.value}`}</Typography>
                  <Typography variant={'subtitle2'}>
                    This {primaryLabel.value} will be converted to {getIndefiniteArticle(secondaryLabel.value)}{' '}
                    {secondaryLabel.value}. A new ID will be assigned, it will be removed from the{' '}
                    {primaryLabel.plural.toLowerCase()} section, and it will appear in the{' '}
                    {secondaryLabel.plural.toLowerCase()} section.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" onClick={handleConvert}>
                    Convert to {secondaryLabel.value}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={disableConfirm} // we have no validation for TileList, so we'll disable confirm if they're mid-selection
          type="submit"
          form="edit-quote-info-form"
          loading={loading}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )

  function handleConvert() {
    openDialog(ConfirmationDialog, {
      scary: true,
      message: (
        <>
          <Typography>
            This action will convert the current {primaryLabel.value.toLowerCase()} to{' '}
            {getIndefiniteArticle(secondaryLabel.value)} {secondaryLabel.value.toLowerCase()}
          </Typography>
          <Typography>&nbsp;</Typography>
          <Typography>
            The ID will be reset, and it will only appear in the {secondaryLabel.plural.toLowerCase()} section.
          </Typography>
        </>
      ),
      onConfirm: () => {
        dismissDialog()
        const newTerms = getInitialTerms({
          options,
          quoteType: selectedType,
          invoice: !isInvoice
        })
        const updated = Date.now()
        const updatedQuote = {
          ...quote,
          is_invoice: !isInvoice,
          number: null,
          status: {
            ...quote.status,
            history: {
              draft: {
                timestamp: updated,
                day: moment().startOf('day').unix() * 1000,
                firstTimestamp: updated
              }
            },
            updated,
            value: 'draft' as QuoteStatus
          },
          terms: newTerms
        }

        updateQuote({ quote: updatedQuote, autosave: true })
        onConfirm()
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }
}

export default EditQuoteInfoDialog
